import { Fragment, useState } from 'react';
import type {
  StakeModeRoundStats,
  StakeModeStats,
  StakeRoundExplain,
  StakeVolatilityClass,
} from '@sr-test-wizard/shared';
import { ModeDetail } from './ModeDetail';

const COL_COUNT = 10;

function volClass(v: StakeVolatilityClass | null): string {
  if (v === 'HIGH') return 'bad';
  if (v === 'MEDIUM') return 'warn';
  if (v === 'LOW') return 'good';
  return 'muted';
}

function oneIn(n: number | null): string {
  if (n === null || !Number.isFinite(n) || n <= 0) return '—';
  return `1 in ${Math.round(n).toLocaleString()}`;
}

/**
 * Compact per-mode summary table. Clicking a row expands an inline detail panel
 * (OVERVIEW / DISTRIBUTION / STATISTICS) directly beneath that row.
 */
export function ModeStatsTable({
  rows,
  roundsByMode,
  explain,
  explainBusy,
  explainError,
  onExplain,
}: {
  rows: StakeModeStats[];
  roundsByMode?: Map<string, StakeModeRoundStats>;
  explain?: StakeRoundExplain | null;
  explainBusy?: Record<string, boolean>;
  explainError?: Record<string, string>;
  onExplain?: (mode: string) => void;
}) {
  const [expanded, setExpanded] = useState<string | null>(null);
  const toggle = (mode: string) => setExpanded((cur) => (cur === mode ? null : mode));
  const narrativeByMode = new Map((explain?.modes ?? []).map((m) => [m.mode, m.narrative]));

  return (
    <div style={{ overflowX: 'auto', marginTop: 8 }}>
      <table className="data-table mode-table">
        <thead>
          <tr>
            <th>mode</th>
            <th>cost</th>
            <th>vol</th>
            <th>RTP %</th>
            <th>hit %</th>
            <th>win %</th>
            <th>max x</th>
            <th>B-E</th>
            <th>max-win odds</th>
            <th>reconciles</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const isOpen = r.mode === expanded;
            const recon =
              r.reconciles === null ? '—' : r.reconciles ? 'within 0.5pp' : 'off target';
            const reconCls = r.reconciles === false ? 'bad' : r.reconciles ? 'good' : 'muted';
            return (
              <Fragment key={r.mode}>
                <tr
                  className={`row-clickable${isOpen ? ' row-selected' : ''}`}
                  onClick={() => toggle(r.mode)}
                >
                  <td>
                    <span className={`report-chevron${isOpen ? ' open' : ''}`}>▶</span> {r.mode}
                  </td>
                  <td>{r.cost}</td>
                  <td>
                    <span className={`chip ${volClass(r.volatilityClass)}`}>
                      {r.volatilityClass ?? 'N/A'}
                    </span>
                  </td>
                  <td className="mono">{r.rtpPct.toFixed(2)}</td>
                  <td>{r.hitRatePct.toFixed(2)}</td>
                  <td>{(r.winRate * 100).toFixed(2)}</td>
                  <td>{r.maxMultiplier.toLocaleString()}x</td>
                  <td>{oneIn(r.avgSpinsBetweenProfit)}</td>
                  <td>{oneIn(r.maxWinHitRate)}</td>
                  <td className={`small ${reconCls}`}>{recon}</td>
                </tr>
                {isOpen && (
                  <tr className="mode-detail-row">
                    <td colSpan={COL_COUNT}>
                      <ModeDetail
                        row={r}
                        rounds={roundsByMode?.get(r.mode)}
                        explain={
                          onExplain
                            ? {
                                glossary: explain?.glossary,
                                narrative: narrativeByMode.get(r.mode),
                                onExplain,
                                explainBusy: explainBusy?.[r.mode],
                                explainError: explainError?.[r.mode],
                              }
                            : undefined
                        }
                        embedded
                      />
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
